/* על הלב - behaviour layer.
 *
 * Selector-agnostic on purpose: everything keys off the cart form
 * (form[action*="/cart/add"]), which is stable across every Shopify theme,
 * rather than Horizon's internal class names. Missing elements no-op.
 */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Flip to false to let customers order and send the photo later by WhatsApp.
  const REQUIRE_IMAGE = true;

  /* ---------------------------------------------------- launch-price offer */
  /* The whole launch campaign is configured here and nowhere else.
   *
   * LAUNCH_ENDS is the ONE fixed date the price actually rises — the same
   * instant for every visitor, never a per-session reset. Leave it EMPTY and
   * every deadline sentence disappears while the price and badge stay: an
   * unset date must never be able to render as a vague "hurry", which is the
   * invented urgency the brand rules forbid. Set it only when the price will
   * genuinely go up on that day, and then actually raise it.
   *
   * SHOW_POPUP is the kill switch — one word to turn the popup off if it
   * turns out to annoy people. */
  const LAUNCH_ENDS = ''; // e.g. '2026-08-31T23:59:59+03:00'
  const LAUNCH_NEXT_PRICE = '₪129';
  /* Off since 2026-08-06 — the user judged the entry popup unnecessary. The
   * code stays so it is one word to bring back; delete initLaunchPopup and the
   * .alh-pop block in custom.css if the decision settles. */
  const SHOW_POPUP = false;
  const POPUP_DELAY_MS = 2000;
  const POPUP_SEEN_KEY = 'alh_launch_popup_v1';
  const POPUP_TARGET = '/products/dogtag-photo-engraved';

  const MAX_LINE = 18;
  const MAX_BYTES = 20 * 1024 * 1024; // Shopify rejects line-item files over 20MB

  const PROP = {
    top: 'כיתוב עליון',
    bottom: 'כיתוב תחתון',
    image: 'תמונה לחריטה',
  };

  const cartForm = () => document.querySelector('form[action*="/cart/add"]');

  /* Scroll reveal.
   *
   * This deliberately does NOT use IntersectionObserver. Horizon puts sections
   * under `content-visibility: auto`, and a subtree the browser has skipped
   * never reports as intersecting — so the observer simply never fired and 16
   * of 18 elements stayed at opacity:0 forever. On a phone that meant most of
   * the homepage, images included, was permanently invisible.
   *
   * getBoundingClientRect() is unaffected by content-visibility, so a throttled
   * rAF check against the viewport is both simpler and reliable. Two failsafes
   * on top: anything still hidden a few seconds in gets revealed regardless,
   * and if this script throws before wiring up, nothing was ever hidden —
   * the class is only added once the reveal loop is definitely running. */
  function initReveal() {
    if (reduceMotion) return;

    const targets = [
      ...document.querySelectorAll(
        '.shopify-section > * > [class*="group"], .shopify-section img'
      ),
    ].filter((el) => !el.classList.contains('alh-reveal'));
    if (!targets.length) return;

    const pending = [];
    for (const el of targets) {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.9) continue;
      el.classList.add('alh-reveal');
      pending.push(el);
    }
    if (!pending.length) return;

    const show = (el) => {
      el.dataset.inView = 'true';
    };

    let queued = false;
    const check = () => {
      queued = false;
      const limit = window.innerHeight * 0.92;
      for (let i = pending.length - 1; i >= 0; i--) {
        const el = pending[i];
        if (el.getBoundingClientRect().top < limit) {
          show(el);
          pending.splice(i, 1);
        }
      }
      if (!pending.length) cleanup();
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(check);
    };

    /* Horizon sets `overflow-y: hidden` on both <html> and <body> and scrolls
     * an inner `div.page-wrapper` instead, so the document never scrolls:
     * window.scrollY stays 0 and a scroll listener on window NEVER fires.
     * Scroll events don't bubble, but they DO capture — so listen on document
     * in the capture phase and we catch whichever element actually scrolls,
     * without having to hard-code Horizon's wrapper class. */
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll, { passive: true });

    /* Failsafe only — with the capture listener above this should never be what
     * reveals anything. It previously was, which is why the whole page sat
     * blank and then popped in at once a few seconds after load.
     *
     * `stop` must be declared and assigned BEFORE the first check(): if every
     * element is already in view, check() empties `pending` and calls
     * cleanup() synchronously, and a `const stop` declared below would still be
     * in the temporal dead zone — a ReferenceError that would abort init() and
     * take the personalisation panel and sticky add-to-cart down with it. */
    let stop = 0;

    function cleanup() {
      clearTimeout(stop);
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', onScroll);
    }

    stop = setTimeout(() => {
      for (const el of pending.splice(0)) show(el);
      cleanup();
    }, 4000);

    check();
  }

  function buildTextRow(key, label, placeholder) {
    const wrap = document.createElement('label');
    wrap.className = 'alh-field';

    const cap = document.createElement('span');
    cap.className = 'alh-field__label';
    cap.textContent = label;

    const input = document.createElement('input');
    input.type = 'text';
    input.name = 'properties[' + key + ']';
    input.maxLength = MAX_LINE;
    input.placeholder = placeholder;
    input.className = 'alh-field__input';
    input.autocomplete = 'off';

    const count = document.createElement('span');
    count.className = 'alh-field__count';
    const sync = () => {
      count.textContent = input.value.length + '/' + MAX_LINE;
    };
    input.addEventListener('input', sync);
    sync();

    wrap.append(cap, input, count);
    return wrap;
  }

  function buildImageRow() {
    const wrap = document.createElement('div');
    wrap.className = 'alh-field alh-field--file';

    const cap = document.createElement('span');
    cap.className = 'alh-field__label';
    cap.textContent = REQUIRE_IMAGE ? 'תמונה לחריטה (חובה)' : 'תמונה לחריטה';

    const drop = document.createElement('label');
    drop.className = 'alh-drop';

    const input = document.createElement('input');
    input.type = 'file';
    input.name = 'properties[' + PROP.image + ']';
    input.accept = 'image/*';
    input.className = 'alh-drop__input';

    const hint = document.createElement('span');
    hint.className = 'alh-drop__hint';
    hint.textContent = 'בחרו תמונה מהטלפון';

    const preview = document.createElement('img');
    preview.className = 'alh-drop__preview';
    preview.alt = '';
    preview.hidden = true;

    const error = document.createElement('p');
    error.className = 'alh-field__error';
    error.hidden = true;

    input.addEventListener('change', () => {
      error.hidden = true;
      const file = input.files && input.files[0];
      if (!file) {
        preview.hidden = true;
        hint.textContent = 'בחרו תמונה מהטלפון';
        return;
      }
      if (file.size > MAX_BYTES) {
        input.value = '';
        preview.hidden = true;
        error.textContent = 'הקובץ גדול מדי. עד 20MB.';
        error.hidden = false;
        return;
      }
      preview.src = URL.createObjectURL(file);
      preview.hidden = false;
      hint.textContent = file.name;
    });

    drop.append(input, preview, hint);
    wrap.append(cap, drop, error);
    return wrap;
  }

  function initPersonalisation() {
    const form = cartForm();
    if (!form || form.dataset.alhPersonalised === 'true') return;

    const submit = form.querySelector('button[type="submit"], [type="submit"]');
    if (!submit) return;

    form.dataset.alhPersonalised = 'true';
    form.setAttribute('enctype', 'multipart/form-data');

    const panel = document.createElement('div');
    panel.className = 'alh-personalise';

    const path = window.location.pathname;
    const isKeychain = /keychain/i.test(path);
    const isFamilyPack = /family-pack/i.test(path);

    /* One panel serves all three products, so the wording has to name the
     * right object. Calling a keychain "הדיסקית" was simply wrong, and on the
     * four-tag pack the singular made the form look broken — a buyer could not
     * tell whether one photo covered all four or whether fields were missing.
     * The pack ships four IDENTICAL tags, so one photo and one pair of lines
     * is correct; it just has to say so. */
    const copy = isKeychain
      ? {
          title: 'מה נחרוט על המחזיק',
          sub: 'שתי שורות והתמונה. אנחנו מכינים אותה לחריטה — אתם לא צריכים לערוך כלום.',
        }
      : isFamilyPack
        ? {
            title: 'מה נחרוט על ארבע הדיסקיות',
            sub: 'שתי שורות ותמונה אחת — כל ארבע הדיסקיות יוצאות זהות.',
          }
        : {
            title: 'מה נחרוט על הדיסקית',
            sub: 'שתי שורות והתמונה. אנחנו מכינים אותה לחריטה — אתם לא צריכים לערוך כלום.',
          };

    const head = document.createElement('div');
    head.className = 'alh-personalise__head';
    const title = document.createElement('strong');
    title.textContent = copy.title;
    const sub = document.createElement('span');
    sub.textContent = copy.sub;
    head.append(title, sub);

    // Placeholders must echo the wording engraved in that product's own
    // photos, or the example on screen contradicts the example in the image.
    const sample = isKeychain
      ? { top: 'איתך בכל דרך', bottom: 'אוהבים, המשפחה' }
      : { top: 'איתך תמיד', bottom: 'אוהבת, אמא' };

    const topRow = buildTextRow(PROP.top, 'שורה עליונה', sample.top);
    const bottomRow = buildTextRow(PROP.bottom, 'שורה תחתונה', sample.bottom);
    const imageRow = buildImageRow();

    panel.append(head, topRow, bottomRow, imageRow);
    submit.parentElement.insertBefore(panel, submit);

    const formError = document.createElement('p');
    formError.className = 'alh-field__error';
    formError.hidden = true;
    panel.appendChild(formError);

    const fileInput = imageRow.querySelector('input[type="file"]');
    const topInput = topRow.querySelector('input');
    const bottomInput = bottomRow.querySelector('input');

    /* Returns null when the order is fillable, otherwise the field to fix.
     * Both lines blank is treated as incomplete: an engraving with no words
     * is almost always someone who missed the panel, not a design choice. */
    function firstProblem() {
      if (REQUIRE_IMAGE && !(fileInput.files && fileInput.files[0])) {
        return { el: fileInput, msg: 'צריך לצרף תמונה לחריטה לפני שממשיכים.' };
      }
      if (!topInput.value.trim() && !bottomInput.value.trim()) {
        return { el: topInput, msg: 'כתבו לפחות שורה אחת שתיחרט על הדיסקית.' };
      }
      return null;
    }

    function reject(problem) {
      formError.textContent = problem.msg;
      formError.hidden = false;
      panel.dataset.invalid = 'true';
      panel.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'center',
      });
      problem.el.focus({ preventScroll: true });
    }

    for (const el of [fileInput, topInput, bottomInput]) {
      el.addEventListener('input', () => {
        if (!firstProblem()) {
          formError.hidden = true;
          delete panel.dataset.invalid;
        }
      });
    }

    /* We validate. The theme adds. Nothing here touches the cart itself.
     *
     * History, because it cost us real money once: an early version let the
     * click through AND posted FormData on submit, so both paths ran and a QA
     * order came out as 2 x ₪179 = ₪358 with the photo uploaded twice. The fix
     * at the time was to stop the theme dead and own the POST ourselves,
     * justified by "a plain AJAX cart drops File values".
     *
     * That justification was wrong for Horizon. product-form.js posts
     * `new FormData(form)` — which carries the File — and fetchConfig
     * ('javascript') deletes Content-Type so the browser writes the multipart
     * boundary itself. The theme could always have carried the photo.
     *
     * Owning the POST then cost us the cart drawer. Horizon's own path
     * dispatches CartLinesUpdateEvent and asks the server for `sections`, and
     * that pair is what re-renders the cart and springs the drawer open. A raw
     * fetch fires neither, so the drawer would sit shut and stale while the
     * item silently landed in the cart.
     *
     * So the interception is now one-way only: block when the order cannot be
     * filled, and otherwise get out of the theme's way entirely. */

    function intercept(event) {
      const problem = firstProblem();
      if (!problem) return; // fillable — let Horizon add it and open the drawer
      event.preventDefault();
      event.stopImmediatePropagation();
      reject(problem);
    }

    document.addEventListener(
      'click',
      (event) => {
        const btn = event.target.closest && event.target.closest(
          'form[action*="/cart/add"] [type="submit"], .alh-sticky-atc__btn'
        );
        if (!btn) return;
        intercept(event);
      },
      true
    );

    // Keyboard submits (Enter in a text field) never produce a click.
    form.addEventListener('submit', intercept, true);
  }

  /* ------------------------------------------------- quantity as choices ---
   * The bare +/- stepper floating above the personalisation panel read as a
   * stray control — nothing on screen said what it was counting. Replaced
   * with a labelled row of choices built in the EXACT markup Horizon uses for
   * "מין החייל" and "צבע", so it inherits the theme's button styling,
   * selected state and focus ring without a line of new visual CSS.
   *
   * Worth knowing how the selected state works, because it looks like it
   * needs JS and doesn't: each label carries an absolutely-positioned
   * .variant-option__button-label__pill painted in the selected colour, and
   * every label has `overflow: clip`. The theme's sibling rules translate the
   * pill out of its own box for every label except the checked one, whose
   * pill stays at --pill-offset: 0 and fills it. All of it keys off :checked,
   * so it behaves correctly without variant-picker.js ever being involved.
   *
   * The native stepper is NOT removed. It is the field that actually carries
   * `quantity` in the form post, so it stays in the DOM and is only hidden —
   * and "עוד" brings it back for the rare order above four. */
  const QTY_CHOICES = [1, 2, 3, 4];

  function initQuantityChoice() {
    const form = cartForm();
    if (!form) return;

    const existing = form.querySelector('.alh-qty');
    if (existing && existing.isConnected) return;

    const row = form.querySelector('.product-form-buttons');
    const wrapper = form.querySelector('.quantity-selector-wrapper');
    const input = form.querySelector('input[name="quantity"]');
    if (!row || !wrapper || !input) return;

    const field = document.createElement('fieldset');
    field.className =
      'variant-option variant-option--buttons variant-option--equal-width-buttons alh-qty';
    /* The theme emits --variant-ch per option from the longest value. Without
     * it the grid's minmax() calc is invalid, which drops
     * grid-template-columns entirely and stacks the buttons vertically. */
    field.style.setProperty('--variant-ch', '0.65em');

    const legend = document.createElement('legend');
    legend.textContent = 'כמה יחידות תרצו?';
    field.appendChild(legend);

    const groupName = 'alh-qty-' + (form.id || 'product');
    const start = parseInt(input.value, 10) || 1;
    const radios = new Map();

    const apply = (n) => {
      input.value = String(n);
      // Horizon reads the field on blur; fire both so any listener syncs.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    for (const n of QTY_CHOICES) {
      const label = document.createElement('label');
      label.className = 'variant-option__button-label';

      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = groupName;
      radio.value = String(n);
      radio.checked = n === start;
      radio.addEventListener('change', () => apply(n));

      const pill = document.createElement('span');
      pill.className = 'variant-option__button-label__pill';

      const text = document.createElement('span');
      text.className = 'variant-option__button-label__text';
      text.textContent = String(n);

      label.append(radio, pill, text);
      field.appendChild(label);
      radios.set(n, radio);
    }

    /* Above four is rare enough that four buttons plus an escape hatch beats
     * a longer row. "עוד" hands the customer back the stepper rather than
     * capping what they can order. */
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'variant-option__button-label alh-qty__more';
    more.textContent = 'עוד';
    more.addEventListener('click', () => {
      row.dataset.alhQty = 'stepper';
      for (const radio of radios.values()) radio.checked = false;
      input.focus();
    });
    field.appendChild(more);

    /* Typing in the revealed stepper must not leave a button looking chosen
     * when it no longer matches. */
    input.addEventListener('input', () => {
      const value = parseInt(input.value, 10);
      for (const [n, radio] of radios) radio.checked = n === value;
    });

    row.dataset.alhQty = 'buttons';
    wrapper.insertAdjacentElement('beforebegin', field);
    if (start > QTY_CHOICES[QTY_CHOICES.length - 1]) row.dataset.alhQty = 'stepper';
  }

  /* ------------------------------------------------------ cart: photo link ---
   * Shopify saves a line-item upload under a hashed name and the theme prints
   * it verbatim, so the cart showed
   * "תמונה לחריטה: 28fcc12c71b81e62340575e32ae41417.png".
   * On the one screen where the buyer is meant to check what will be cut into
   * metal, that reads as a glitch. Swap the text for words and keep the raw
   * name on hover; the href is untouched, so the link still opens the exact
   * file that goes to production. */
  function initCartUploads() {
    const links = document.querySelectorAll('.cart-items__property dd a[href*="/uploads/"]');
    for (const link of links) {
      if (link.dataset.alhUpload === 'true') continue;
      const raw = (link.textContent || '').trim();
      link.dataset.alhUpload = 'true';
      if (raw) link.title = raw;
      link.textContent = 'צפייה בתמונה שצירפתם';

      /* The drawer took over from /cart as the last screen before payment, so
       * the attached face has to be *visible* there. A filename is not a check
       * — nobody catches "I uploaded the wrong photo" by reading a hash. The
       * href is already a public CDN URL, so the thumbnail costs no upload and
       * no extra request beyond the image itself. */
      const dd = link.parentElement;
      if (!dd || dd.querySelector('.alh-cart-thumb')) continue;
      const thumb = document.createElement('img');
      thumb.className = 'alh-cart-thumb';
      thumb.src = link.href;
      thumb.alt = 'התמונה שצירפתם לחריטה';
      thumb.loading = 'lazy';
      dd.insertBefore(thumb, link);
    }
  }

  /* -------------------------------------------------------------- swatches */
  /* Shopify only renders native colour swatches when the option is linked to a
   * colour metaobject, which is a whole taxonomy setup. The finishes are a
   * closed set of three, so paint them here instead — the label text is the
   * key, and anything unrecognised is left exactly as the theme rendered it. */
  const FINISH = {
    'אפור': 'linear-gradient(135deg,#f4f5f6,#b9bcc0 42%,#eceef0 58%,#94999f)',
    'זהב': 'linear-gradient(135deg,#f7e6b4,#c9a227 42%,#f1da8d 58%,#9d7a1f)',
    'שחור': 'linear-gradient(135deg,#4d4d4a,#1c1c1a 42%,#3b3b38 58%,#0f0f0e)',
  };

  function initSwatches() {
    const labels = document.querySelectorAll(
      'fieldset label, [class*="variant"] label, [class*="option"] label'
    );

    for (const label of labels) {
      if (label.dataset.alhSwatch) continue;

      const name = (label.textContent || '').trim();
      const paint = FINISH[name];
      if (!paint) continue;

      label.dataset.alhSwatch = 'true';
      label.classList.add('alh-swatch-label');

      // The colour is the label — a word next to a gold square just repeats it.
      // Keep the name reachable for screen readers and on hover.
      label.setAttribute('aria-label', name);
      label.setAttribute('title', name);

      const dot = document.createElement('span');
      dot.className = 'alh-swatch';
      dot.style.background = paint;
      dot.setAttribute('aria-hidden', 'true');
      label.prepend(dot);
    }
  }

  /* Horizon re-renders the variant picker on every selection, so the labels
   * that were decorated at load are thrown away and replaced with plain ones.
   * Watch for that and redecorate; initSwatches is idempotent. */
  function watchSwatches() {
    if (document.body.dataset.alhSwatchWatch === 'true') return;
    document.body.dataset.alhSwatchWatch = 'true';

    let queued = false;
    const observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        /* This observer is the shared "re-apply our decorations" hook — every
         * function below is idempotent and returns immediately when its work
         * is already done, so the cost per mutation is a failed selector. */
        initSwatches();
        // A variant change re-renders the buy area and can drop the row.
        initQuantityChoice();
        // Changing a quantity re-renders the cart lines and their properties.
        initCartUploads();
        // The drawer is rebuilt from server HTML on every cart change, which
        // throws the journey away with it. Rebuild it in the same pass.
        initDrawerJourney();
        // The terms checkbox lives in the same rebuilt drawer.
        initTermsGate();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function initStickyAddToCart() {
    if (document.querySelector('.alh-sticky-atc')) return;

    const form = cartForm();
    if (!form) return;

    const realButton = form.querySelector('button[type="submit"], [type="submit"]');
    if (!realButton) return;

    const titleEl = document.querySelector('h1');
    const priceEl = document.querySelector('[class*="price"]');

    const bar = document.createElement('div');
    bar.className = 'alh-sticky-atc';
    bar.setAttribute('aria-hidden', 'true');

    const meta = document.createElement('div');
    meta.className = 'alh-sticky-atc__meta';
    const name = document.createElement('strong');
    name.textContent = (titleEl ? titleEl.textContent : '').trim();
    const price = document.createElement('span');
    price.textContent = (priceEl ? priceEl.textContent : '').trim();
    meta.append(name, price);

    const proxy = document.createElement('button');
    proxy.type = 'button';
    proxy.className = 'alh-sticky-atc__btn';
    proxy.textContent = (realButton.textContent || 'הוספה לסל').trim();

    // Forward to the real button; the document-level guard above decides
    // whether that click is allowed to reach the cart.
    proxy.addEventListener('click', () => realButton.click());

    bar.append(meta, proxy);
    document.body.appendChild(bar);

    const syncState = () => {
      proxy.disabled = realButton.disabled;
      const label = (realButton.textContent || '').trim();
      if (label) proxy.textContent = label;
    };
    new MutationObserver(syncState).observe(realButton, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    syncState();

    if (!('IntersectionObserver' in window)) return;

    /* The signup tab is fixed to the same bottom-left corner at a higher
     * z-index, so while this bar is up it has to be told to step over it.
     * Publishing the MEASURED height (not a constant) keeps the two in step if
     * the bar's padding or the safe-area inset ever changes. */
    const publishHeight = () => {
      /* Above 750px the bar is display:none and measures 0, and writing that
       * would tell the CSS to lift the tab by nothing at all. Only ever
       * publish a height the bar actually occupies. */
      const h = bar.offsetHeight;
      if (h > 0) document.body.style.setProperty('--alh-atc-h', h + 'px');
    };

    /* Keep the bar's text clear of the floating accessibility button.
     *
     * Negishot renders into a shadow root at the maximum z-index, so it cannot
     * be reached with CSS and — being a legal requirement — must not be moved.
     * Reading its rect and yielding the space is the only honest fix. Measured
     * rather than hard-coded so the bar reclaims the space if the widget is
     * uninstalled or moves to the other corner; the whole thing is defensive
     * because it reaches into a third party's DOM. */
    const publishPad = () => {
      let pad = 16;
      try {
        const host = document.getElementById('negishot-ui-host');
        const btn = host && host.shadowRoot && host.shadowRoot.querySelector('.negishot-btn');
        const r = btn && btn.getBoundingClientRect();
        // Only yield for a widget that actually sits over the bar's own band.
        if (r && r.width && r.bottom > window.innerHeight - bar.offsetHeight - 24) {
          /* Measure against the BAR's own edge, not innerWidth — they differ by
           * the scrollbar gutter (380 vs 390 here) and that error is 10px of
           * dead space taken straight out of the button. */
          const barRect = bar.getBoundingClientRect();
          const needed =
            document.dir === 'rtl' ? barRect.right - r.left : r.right - barRect.left;
          pad = Math.min(Math.max(needed + 8, 16), barRect.width * 0.28);
        }
      } catch (err) {
        /* A third-party widget is allowed to change shape; the bar is not
         * allowed to break when it does. */
      }
      document.body.style.setProperty('--alh-atc-pad', Math.round(pad) + 'px');
    };
    publishPad();
    setTimeout(publishPad, 1500); // the widget mounts asynchronously

    const io = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.dataset.visible = String(show);
        bar.setAttribute('aria-hidden', String(!show));
        if (show) publishHeight();
        document.body.classList.toggle('alh-atc-open', show);
      },
      { threshold: 0 }
    );
    io.observe(realButton);

    /* Rotating the phone changes the bar's height (the label can wrap) while
     * the bar is already up, and the observer will not fire again for that. */
    window.addEventListener('resize', () => {
      if (bar.dataset.visible === 'true') publishHeight();
      publishPad();
    });
  }

  /* ------------------------------------------------------- contact form ---
   * Two jobs: confirm loudly on success, and stop the same person firing the
   * form repeatedly.
   *
   * IMPORTANT, and stated plainly because it is easy to over-trust: the
   * cooldown is localStorage, so it is a COURTESY GUARD, not spam protection.
   * It stops accidental double-sends and casual repeat-senders. Anyone who
   * clears storage, opens a private window or switches browser walks straight
   * through it. The thing actually blocking abuse is Shopify's own bot
   * challenge on POST /contact, which is server-side and already active. */
  const CONTACT_KEY = 'alh:contact:last';
  const COOLDOWN_MS = 6 * 60 * 60 * 1000; // 6 hours

  function contactCooldownLeft() {
    try {
      const last = parseInt(localStorage.getItem(CONTACT_KEY) || '0', 10);
      if (!last) return 0;
      return Math.max(0, COOLDOWN_MS - (Date.now() - last));
    } catch (e) {
      return 0; // storage blocked (private mode) — never lock the user out
    }
  }

  function showContactPopup(message, tone) {
    if (document.querySelector('.alh-modal')) return;

    const overlay = document.createElement('div');
    overlay.className = 'alh-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    const box = document.createElement('div');
    box.className = 'alh-modal__box';
    if (tone) box.dataset.tone = tone;

    if (tone !== 'wait') {
      const mark = document.createElement('div');
      mark.className = 'alh-modal__mark';
      mark.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
        'stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 12.5l5 5 10-11"/></svg>';
      box.appendChild(mark);
    }

    const title = document.createElement('strong');
    title.textContent = tone === 'wait' ? 'כבר קיבלנו את הפנייה שלכם' : 'נשלח בהצלחה';
    const body = document.createElement('p');
    body.textContent = message;

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'alh-modal__btn';
    close.textContent = 'סגירה';

    const dismiss = () => {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    close.addEventListener('click', dismiss);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) dismiss();
    });
    document.addEventListener('keydown', onKey);

    box.append(title, body, close);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    close.focus();
  }

  function initContactForm() {
    const form = document.querySelector('form[action*="/contact"]');

    /* Shopify posts the form, then reloads the page with ?contact_posted=true.
     * That round trip is the only reliable success signal — there is no AJAX
     * response to hook. */
    const posted = /[?&]contact_posted=true/.test(window.location.search);
    if (posted) {
      try {
        localStorage.setItem(CONTACT_KEY, String(Date.now()));
      } catch (e) {
        /* storage unavailable — the popup still shows */
      }
      showContactPopup(
        'ההודעה שלכם התקבלה. הצוות שלנו יחזור אליכם במהירות, בדרך כלל תוך יום עסקים אחד.',
        'ok'
      );
      // Drop the flag so a refresh doesn't replay the popup.
      if (window.history.replaceState) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    }

    if (!form || form.dataset.alhContact === 'true') return;
    form.dataset.alhContact = 'true';

    const applyCooldown = () => {
      const left = contactCooldownLeft();
      const fields = form.querySelectorAll('input:not([type="hidden"]), textarea, button');
      if (left <= 0) {
        for (const el of fields) el.disabled = false;
        const n = form.querySelector('.alh-contact-wait');
        if (n) n.remove();
        return false;
      }
      for (const el of fields) el.disabled = true;
      if (!form.querySelector('.alh-contact-wait')) {
        const hours = Math.ceil(left / (60 * 60 * 1000));
        const note = document.createElement('p');
        note.className = 'alh-contact-wait';
        note.textContent =
          'כבר שלחתם לנו הודעה. אנחנו עליה — אפשר לשלוח שוב בעוד כ־' +
          hours +
          ' שעות. אם זה דחוף, השיבו למייל שקיבלתם.';
        form.prepend(note);
      }
      return true;
    };

    applyCooldown();

    form.addEventListener(
      'submit',
      (event) => {
        if (contactCooldownLeft() > 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          applyCooldown();
          showContactPopup(
            'כבר קיבלנו ממכם פנייה לאחרונה ואנחנו מטפלים בה. נחזור אליכם בהקדם.',
            'wait'
          );
        }
        /* On a genuine send we do NOT stamp the clock here — only a real
         * ?contact_posted=true does. Stamping on submit would lock someone
         * out for six hours after a validation failure that never reached us. */
      },
      true
    );
  }

  /* ---------------------------------------------------- reviews carousel ---
   * Mobile only: one card per screen with arrows, the achva pattern. Native
   * scroll-snap stays underneath so swiping still works and the whole thing
   * degrades to a plain scroller if this script never runs.
   *
   * Navigation is index + scrollIntoView rather than arithmetic on scrollLeft:
   * under RTL, scrollLeft is negative in some engines and reversed in others,
   * and hand-rolled maths there is exactly how carousels end up jumping to the
   * wrong card. scrollIntoView has no such ambiguity. */
  function initReviewsCarousel() {
    const track = document.querySelector('.alh-rev__grid--quotes');
    if (!track || track.dataset.alhCarousel === 'true') return;

    const cards = [...track.querySelectorAll('.alh-rev__card')];
    if (cards.length < 2) return; // a lone review needs no controls
    track.dataset.alhCarousel = 'true';

    const nav = document.createElement('div');
    nav.className = 'alh-rev__nav';

    const mkArrow = (dir, label) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'alh-rev__arrow';
      b.dataset.dir = dir;
      b.setAttribute('aria-label', label);
      b.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>';
      return b;
    };
    // In RTL the visually-right arrow walks backwards through the list.
    const prev = mkArrow('prev', 'הביקורת הקודמת');
    const next = mkArrow('next', 'הביקורת הבאה');

    const dots = document.createElement('div');
    dots.className = 'alh-rev__dots';
    const dotEls = cards.map((_, i) => {
      const d = document.createElement('button');
      d.type = 'button';
      d.className = 'alh-rev__dot';
      d.setAttribute('aria-label', 'ביקורת ' + (i + 1));
      d.addEventListener('click', () => go(i));
      dots.appendChild(d);
      return d;
    });

    nav.append(prev, dots, next);
    track.parentElement.insertBefore(nav, track.nextSibling);

    let index = 0;
    const go = (i) => {
      index = Math.max(0, Math.min(cards.length - 1, i));
      cards[index].scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
      sync();
    };
    prev.addEventListener('click', () => go(index - 1));
    next.addEventListener('click', () => go(index + 1));

    function currentIndex() {
      const mid = track.getBoundingClientRect().left + track.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((c, i) => {
        const r = c.getBoundingClientRect();
        const dist = Math.abs(r.left + r.width / 2 - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    function sync() {
      dotEls.forEach((d, i) => {
        d.dataset.active = String(i === index);
      });
      prev.disabled = index === 0;
      next.disabled = index === cards.length - 1;
    }

    let queued = false;
    track.addEventListener(
      'scroll',
      () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          index = currentIndex();
          sync();
        });
      },
      { passive: true }
    );

    sync();
  }

  /* Hebrew-language notice, tucked into the far-left end of the announcement
   * bar. Not a strip of its own — it should be findable, not noticeable.
   * Placed inside the bar (position: relative) so it can be absolutely pinned
   * to the corner without a wrapper of its own. */
  function initLanguageNotice() {
    if (document.querySelector('.alh-gender-note')) return;
    const bar =
      document.querySelector('[id*="header_announcements"] .announcement-bar') ||
      document.querySelector('[id*="header_announcements"]');
    if (!bar) return;
    const note = document.createElement('span');
    note.className = 'alh-gender-note';
    note.textContent = '* האתר כתוב בלשון זכר מטעמי נוחות בלבד, אך מתייחס לשני המינים כאחד.';
    bar.appendChild(note);
  }

  /* The launch-price countdown lived here until 2026-08-06 and was removed at
   * the user's request — the hero shows the price alone now. If a countdown is
   * ever wanted again, it must count to ONE fixed date shared by every visitor
   * (never an evergreen per-session reset, which is the fake-urgency pattern
   * the brand rules forbid) and it must not resize .alh-launch: above 1100px
   * that block is absolutely positioned over the hero photo. */

  /* Formats LAUNCH_ENDS as a Hebrew date, or returns '' when no date is set.
   * Every deadline sentence on the site routes through this, so an unset date
   * silently removes the claim instead of printing something vague. */
  function launchDeadline() {
    if (!LAUNCH_ENDS) return '';
    const t = Date.parse(LAUNCH_ENDS);
    if (!t || t <= Date.now()) return ''; // past deadlines must never render
    return new Date(t).toLocaleDateString('he-IL', {
      day: 'numeric',
      month: 'long',
    });
  }

  /* Delivery deadline — the honest form of urgency on this store.
   *
   * The real pressure here was never the price, it is the calendar: a gift for
   * a swearing-in or a beret march has a date the buyer genuinely cannot miss,
   * and production runs 7–12 days. So instead of inventing a countdown, state
   * the arrival date that ordering *today* buys. It moves on its own every day,
   * it is true every day, and nobody has to be lied to.
   *
   * Deliberately quotes the SLOWER end of the range (12, not 7) — this is a
   * promise a customer will hold the merchant to, so it under-promises.
   * These are CALENDAR days as the merchant stated them on 2026-09-01, so no
   * weekend skipping — do not reintroduce it without re-confirming the unit. */
  const DELIVERY_MAX_DAYS = 12;

  function deliveryEtaText() {
    const d = new Date();
    d.setDate(d.getDate() + DELIVERY_MAX_DAYS);
    return d.toLocaleDateString('he-IL', { day: 'numeric', month: 'long' });
  }

  function initDeliveryEta() {
    const anchor = document.querySelector('.alh-offer');
    if (!anchor || document.querySelector('.alh-eta')) return;

    const row = document.createElement('p');
    row.className = 'alh-eta';
    row.innerHTML =
      '<span class="alh-eta__ico" aria-hidden="true">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ' +
      'stroke-linecap="round" stroke-linejoin="round">' +
      '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg></span>' +
      '<span>הזמנה היום — אצלכם עד <strong>' +
      deliveryEtaText() +
      '</strong></span>';
    anchor.insertAdjacentElement('beforebegin', row);
  }

  /* Launch-price popup.
   *
   * A native <dialog> on purpose: showModal() gives a real focus trap, Esc to
   * close and an inert background for free, which a hand-rolled div does not.
   *
   * Deliberately restrained, because an entry popup is the easiest thing on a
   * store to make hostile:
   *  - waits POPUP_DELAY_MS so the page paints first and the visitor knows
   *    where they are before anything covers it,
   *  - shows once per visitor and remembers the dismissal,
   *  - never appears on product, cart or checkout pages — someone already
   *    looking at the product does not need to be sold the product,
   *  - a bottom sheet on phones rather than a full-screen takeover, which is
   *    also what keeps it clear of Google's intrusive-interstitial penalty,
   *  - closes on Esc, on the X, and on a click outside the panel.
   *
   * If localStorage throws (private mode, cookies blocked) the popup simply
   * does not show. Failing closed is right: a popup that cannot remember it
   * was dismissed would reappear on every page and become the exact nuisance
   * this is written to avoid. */
  function initLaunchPopup() {
    if (!SHOW_POPUP) return;
    if (document.querySelector('.alh-pop')) return;

    const path = location.pathname;
    if (/^\/(products|cart|checkout|account)(\/|$)/.test(path)) return;

    let store = null;
    try {
      store = window.localStorage;
      if (store.getItem(POPUP_SEEN_KEY)) return;
    } catch (err) {
      return;
    }

    const deadline = launchDeadline();

    const dlg = document.createElement('dialog');
    dlg.className = 'alh-pop';
    dlg.setAttribute('aria-labelledby', 'alh-pop-title');

    const panel = document.createElement('div');
    panel.className = 'alh-pop__panel';

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'alh-pop__close';
    close.setAttribute('aria-label', 'סגירה');
    close.textContent = '×';

    const tag = document.createElement('span');
    tag.className = 'alh-pop__tag';
    tag.textContent = 'מחיר השקה';

    const title = document.createElement('p');
    title.className = 'alh-pop__title';
    title.id = 'alh-pop-title';
    title.innerHTML =
      '<span class="alh-pop__now">₪99</span><span class="alh-pop__was">₪129</span>';

    const body = document.createElement('p');
    body.className = 'alh-pop__body';
    body.textContent =
      'שרשרת דיסקית צבאית עם הפנים שלו, חרוטה בלייזר, ושתי שורות שאתם כותבים.';

    const cta = document.createElement('a');
    cta.className = 'alh-pop__cta';
    cta.href = POPUP_TARGET;
    cta.textContent = 'לצפייה במוצר';

    panel.append(close, tag, title, body);

    /* Only rendered when a real date is set — see launchDeadline(). */
    if (deadline) {
      const note = document.createElement('p');
      note.className = 'alh-pop__note';
      note.textContent = 'המחיר עולה ל־' + LAUNCH_NEXT_PRICE + ' ב־' + deadline;
      panel.append(note);
    }

    panel.append(cta);
    dlg.append(panel);
    document.body.append(dlg);

    const remember = () => {
      try {
        store.setItem(POPUP_SEEN_KEY, '1');
      } catch (err) {
        /* nothing sensible to do; the popup is already open */
      }
    };

    const dismiss = () => {
      remember();
      if (dlg.open) dlg.close();
    };

    close.addEventListener('click', dismiss);
    /* Following the CTA counts as answering the popup, so it must not be
     * offered again on the next page. */
    cta.addEventListener('click', remember);
    dlg.addEventListener('cancel', remember); // Esc
    /* A click that lands on the dialog itself rather than the panel is the
     * backdrop. */
    dlg.addEventListener('click', (event) => {
      if (event.target === dlg) dismiss();
    });

    setTimeout(() => {
      if (!document.body.contains(dlg)) return;
      dlg.showModal();
      /* Opening with focus on the close button means the first thing Tab or a
       * screen reader reaches is the way out, not the offer. */
      close.focus();
    }, reduceMotion ? 0 : POPUP_DELAY_MS);
  }

  /* The "אודותינו" page.
   *
   * Two jobs: stagger-reveal the blocks as they scroll in, and fill the beaded
   * chain rail next to the four steps in proportion to how far the section has
   * travelled. Both ride one rAF-throttled handler.
   *
   * Same two rules as initReveal(): the hiding class is added by JS (so a
   * script failure leaves a fully readable page rather than a blank one), and
   * the listener is capture-phase on `document` because Horizon scrolls an
   * inner .page-wrapper at desktop widths, where a window listener never
   * fires. */
  function initAbout() {
    const root = document.querySelector('.alh-ab');
    if (!root || root.dataset.abInit) return;
    root.dataset.abInit = '1';

    const rail = root.querySelector('.alh-ab__rail i');
    const steps = root.querySelector('.alh-ab__steps');
    let lastFill = -1;

    let pending = [];
    if (!reduceMotion) {
      const marks = [...root.querySelectorAll('[data-rv]')];
      /* Delay is per-group, not per-page: siblings cascade, but a block far
       * down the page must not inherit a two-second wait from the hero. */
      const groups = new Map();
      for (const el of marks) {
        const band = el.closest('.alh-ab__band') || root;
        const n = groups.get(band) || 0;
        groups.set(band, n + 1);
        el.style.setProperty('--d', Math.min(n, 6) * 90 + 'ms');
        el.classList.add('alh-ab-rv');
      }
      pending = marks;
    }

    const show = (el) => {
      el.dataset.inView = 'true';
    };

    let queued = false;

    const drop = (el) => {
      const i = pending.indexOf(el);
      if (i > -1) pending.splice(i, 1);
      show(el);
    };

    const frame = () => {
      queued = false;
      const vh = window.innerHeight;

      for (let i = pending.length - 1; i >= 0; i--) {
        if (pending[i].getBoundingClientRect().top < vh * 0.88) {
          show(pending[i]);
          pending.splice(i, 1);
        }
      }

      if (rail && steps) {
        const r = steps.getBoundingClientRect();
        /* 0 when the list's top reaches 78% of the viewport, 1 once its
         * bottom passes 40% — the chain fills roughly as the eye travels. */
        const start = vh * 0.78;
        const end = vh * 0.4;
        const span = r.height + (start - end);
        const p = span > 0 ? (start - r.top) / span : 1;
        const fill = Math.round(Math.max(0, Math.min(1, p)) * 200) / 2;
        /* Writing --fill animates a `block-size`, which invalidates layout.
         * Above and below the section the clamped value is a constant 0 or
         * 100, so without this guard every scroll frame on the rest of the
         * page paid for a layout that changed nothing. On the product page
         * the journey sits at the very bottom, which means almost the whole
         * scroll would have been spent that way. */
        if (fill !== lastFill) {
          lastFill = fill;
          rail.style.setProperty('--fill', fill + '%');
        }
      }
    };

    const onScroll = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(frame);
    };

    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onScroll, { passive: true });

    /* No time-based failsafe here, deliberately.
     *
     * The homepage's initReveal() reveals everything still hidden after four
     * seconds, and that is wrong for this block: a timer cannot tell "the
     * reveal mechanism is dead" apart from "the visitor simply hasn't scrolled
     * down yet". On the homepage the card block sits far below the fold, so
     * the timer fired every single time and the cards were already faded in
     * before anyone reached them.
     *
     * Redundancy instead of a timer: an IntersectionObserver runs alongside
     * the rAF check, both writing the same attribute. If one channel fails the
     * other still reveals the content. (IntersectionObserver alone is not
     * enough — under `content-visibility: auto` a skipped subtree never
     * reports as intersecting, which is what left 16 elements permanently
     * invisible on the homepage.) */
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue;
            io.unobserve(e.target);
            drop(e.target);
          }
        },
        { rootMargin: '0px 0px -10% 0px' }
      );
      for (const el of pending) io.observe(el);
    }

    frame();
    /* Images and webfonts land after DOMContentLoaded and move everything
     * below them; re-measure once the page has actually settled. */
    window.addEventListener('load', onScroll, { once: true });
  }

  /* ================================================= cart drawer: content ===
   *
   * The drawer used to carry a delivery-date line and a family-pack offer above
   * the items. Both were removed on 2026-08-13 at the user's request: on a
   * 390x844 phone the summary alone ran 458px of a 756px panel, and with the
   * two extra blocks on top the thing the buyer opened the cart to SEE — the
   * product, its two engraved lines and the attached face — was squeezed into
   * the middle. A cart's first job is to show what is in it.
   *
   * The delivery date still lives on the product page; the family-pack offer
   * was also contradicting the live BXGY (four tags cost ₪297 against the
   * pack's ₪299), so removing it settled a false saving as well as the height.
   * Do not reinstate either without being asked. */

  function initDrawerDiscount() {
    const boxes = document.querySelectorAll('.cart-discount details');
    for (const box of boxes) {
      if (!box.open) box.open = true;
      const summary = box.querySelector('summary');
      if (!summary || summary.dataset.alhPinned === 'true') continue;
      summary.dataset.alhPinned = 'true';
      summary.addEventListener('click', (e) => e.preventDefault());
    }
  }

  /* Re-entrant by design: the drawer is re-rendered on every cart change, which
   * throws these nodes away. Each part checks for its own node rather than a
   * flag on the container, so it works whether morph replaces the container or
   * only its children. */
  function initDrawerJourney() {
    initDrawerDiscount();
  }

  /* ------------------------------------------------ newsletter reward ---
   *
   * IDF10 was pulled from the announcement bar and the homepage marquee on
   * 2026-08-12, because a code printed on the page buys no email addresses —
   * nobody trades their inbox for something already on screen. It is now the
   * reward for signing up, and this is the on-site half of that: the moment
   * after the form posts, the code appears on the page instead of only in an
   * inbox the buyer has to go and check.
   *
   * That matters more than it sounds. A visitor who has to leave, find a mail,
   * and come back is a visitor who does not come back. Showing it here means
   * they can paste it at checkout in the same minute they gave the address.
   *
   * The code is deliberately NOT unique per subscriber — the user chose one
   * shared code, so nothing here needs a per-customer generator. */
  /* IDF100, not IDF10. Shopify Forms cannot adopt an existing code — it insists
   * on managing its own — so the signup reward became a new code on 2026-08-12.
   * IDF10 was DELETED from the store the same day (it had expired at 00:55 and
   * had zero uses); IDF100 is now the only discount code that exists.
   * If Forms is ever configured to show the code itself, delete this screen
   * rather than leaving two codes on screen disagreeing with each other. */
  const REWARD_CODE = 'IDF100';

  function showRewardModal() {
    if (document.querySelector('.alh-modal')) return;

    const overlay = document.createElement('div');
    overlay.className = 'alh-modal alh-reward';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    const box = document.createElement('div');
    box.className = 'alh-modal__box';

    const mark = document.createElement('div');
    mark.className = 'alh-modal__mark';
    mark.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" ' +
      'stroke-linecap="round" stroke-linejoin="round"><path d="M4.5 12.5l5 5 10-11"/></svg>';

    const title = document.createElement('strong');
    title.className = 'alh-reward__title';
    title.textContent = 'מעולה!';

    const body = document.createElement('p');
    body.className = 'alh-reward__lead';
    body.textContent = 'הקוד שלכם מוכן — שלחנו אותו גם למייל.';

    /* The whole dashed box is the copy target, not a small button beside it —
     * on a phone the code itself is the thing a thumb goes for. */
    const codeBox = document.createElement('button');
    codeBox.type = 'button';
    codeBox.className = 'alh-reward__code';
    codeBox.setAttribute('aria-label', 'העתקת קוד ההנחה ' + REWARD_CODE);
    codeBox.innerHTML =
      '<svg class="alh-reward__ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<rect x="9" y="9" width="11" height="11" rx="2"/>' +
      '<path d="M5 15V6a2 2 0 0 1 2-2h9"/></svg>' +
      '<span class="alh-reward__value">' +
      REWARD_CODE +
      '</span>';

    const code = codeBox.querySelector('.alh-reward__value');

    const note = document.createElement('p');
    note.className = 'alh-reward__note';
    note.textContent = 'למימוש הזינו את קוד הקופון בשלב התשלום';

    codeBox.addEventListener('click', () => {
      const done = () => {
        note.textContent = 'הקוד הועתק ✓';
        codeBox.dataset.done = 'true';
      };
      /* Clipboard access fails on insecure origins and when permission is
       * refused. Never claim success blindly — select the text instead so the
       * buyer can copy it by hand rather than pasting nothing at checkout. */
      const fallback = () => {
        try {
          const range = document.createRange();
          range.selectNodeContents(code);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        } catch (e) {
          /* selection unavailable — the code is still on screen to read */
        }
        note.textContent = 'סמנו את הקוד והעתיקו אותו';
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(REWARD_CODE).then(done, fallback);
      } else {
        fallback();
      }
    });

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'alh-modal__btn';
    close.textContent = 'מעולה, לקנייה';

    const dismiss = () => {
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    close.addEventListener('click', dismiss);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) dismiss();
    });
    document.addEventListener('keydown', onKey);

    box.append(mark, title, body, codeBox, note, close);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    close.focus();
  }

  function initNewsletterReward() {
    if (!/[?&]customer_posted=true/.test(window.location.search)) return;

    /* Strip the flag before showing anything, so a refresh or the back button
     * cannot replay the reward as though a second signup had happened. */
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('customer_posted');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    } catch (e) {
      /* history unavailable — worst case the modal reappears on refresh */
    }

    showRewardModal();
  }

  /* ---------------------------------------------------- signup capture ---
   *
   * Two steps, the shape the reference store uses: sell the offer first, ask
   * for the address only after a yes. Asking cold converts badly — the visitor
   * is being charged before being told what they get.
   *
   * Why this is ours and not Shopify Forms': Forms cannot do a two-step flow,
   * and it exposes NO way to be opened from code — `window.ShopifyForms` has
   * two properties and zero functions, and the custom element has no methods.
   * So "our teaser opens their form" is not buildable; whichever tool starts
   * the flow has to finish it. The email is sent by Shopify Email's
   * "Welcome new subscribers" automation instead, which fires on ANY
   * subscription regardless of which form produced it.
   *
   * SHOW_SIGNUP_POPUP must stay false while the Shopify Forms popup is live,
   * or both open on top of each other — that already happened once.
   *
   * Trap worth keeping: never hand-build a Shopify customer form. hCaptcha
   * only binds to forms Shopify itself rendered, so a hand-written one
   * validates, submits, and silently never posts. Step two therefore MOVES the
   * footer's real form into the panel rather than recreating it. */
  const SHOW_SIGNUP_POPUP = true;
  const SIGNUP_DELAY_MS = 7000;
  const SIGNUP_SEEN_KEY = 'alh_signup_v1';

  function signupSeen() {
    try {
      return localStorage.getItem(SIGNUP_SEEN_KEY) === '1';
    } catch (e) {
      /* Storage blocked. Fail CLOSED: a popup that cannot remember being
       * dismissed would reappear on every page, which is worse than missing
       * one signup. */
      return true;
    }
  }

  function markSignupSeen() {
    try {
      localStorage.setItem(SIGNUP_SEEN_KEY, '1');
    } catch (e) {
      /* see signupSeen() */
    }
  }

  /* Do not interrupt an order in progress. */
  function personalisationStarted() {
    const panel = document.querySelector('.alh-personalise');
    if (!panel) return false;
    const file = panel.querySelector('input[type="file"]');
    if (file && file.files && file.files[0]) return true;
    return [...panel.querySelectorAll('input[type="text"]')].some((i) => i.value.trim() !== '');
  }

  function realNewsletterForm() {
    return [...document.querySelectorAll('form[action*="/contact"]')].find((f) =>
      f.querySelector('input[name="form_type"][value="customer"]')
    );
  }

  let borrowedForm = null;
  let borrowedHome = null;

  function borrowRealForm(container) {
    const real = realNewsletterForm();
    if (!real) return null;
    borrowedForm = real;
    borrowedHome = { parent: real.parentNode, next: real.nextSibling };
    real.classList.add('alh-signup__realform');
    const email = real.querySelector('input[name="contact[email]"]');
    if (email) email.value = '';

    /* The theme renders its success state from `form.posted_successfully?`,
     * which needs a page load — but Shopify's spam protection posts in the
     * background and never navigates, so that reload never comes and the panel
     * would just sit there. Verified on the live store: the submission landed
     * and the customer's marketing consent updated at that exact minute, with
     * no navigation. So hand over to the reward screen on our own clock. If a
     * navigation does happen, the timer dies with the page and
     * initNewsletterReward() shows the same screen from ?customer_posted. */
    real.addEventListener('submit', () => {
      markSignupSeen();
      window.setTimeout(() => {
        if (!document.body.contains(real)) return;
        document.querySelector('.alh-signup')?.remove();
        returnRealForm();
        showRewardModal();
      }, 1800);
    });

    container.appendChild(real);
    return real;
  }

  function returnRealForm() {
    if (!borrowedForm || !borrowedHome || !borrowedHome.parent) {
      borrowedForm = null;
      borrowedHome = null;
      return;
    }
    borrowedForm.classList.remove('alh-signup__realform');
    if (borrowedHome.next && borrowedHome.next.parentNode === borrowedHome.parent) {
      borrowedHome.parent.insertBefore(borrowedForm, borrowedHome.next);
    } else {
      borrowedHome.parent.appendChild(borrowedForm);
    }
    borrowedForm = null;
    borrowedHome = null;
  }

  function openSignupModal() {
    if (document.querySelector('.alh-modal')) return;
    markSignupSeen();

    const overlay = document.createElement('div');
    overlay.className = 'alh-modal alh-signup';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', '10% הנחה על ההזמנה הראשונה');

    const box = document.createElement('div');
    box.className = 'alh-modal__box';

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'alh-signup__close';
    close.setAttribute('aria-label', 'סגירה');
    close.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

    const dismiss = () => {
      returnRealForm(); // the footer must not be left without its signup
      overlay.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    close.addEventListener('click', dismiss);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) dismiss();
    });
    document.addEventListener('keydown', onKey);

    const renderEmail = () => {
      box.innerHTML = '';
      const title = document.createElement('strong');
      title.className = 'alh-signup__title';
      title.textContent = 'לאן לשלוח את הקוד?';
      const sub = document.createElement('p');
      sub.className = 'alh-signup__sub';
      sub.textContent = 'הקוד יופיע כאן מיד, ויישלח גם למייל.';
      box.append(close, title, sub);
      const form = borrowRealForm(box);
      if (!form) {
        dismiss();
        return;
      }
      const email = form.querySelector('input[name="contact[email]"]');
      if (email) email.focus({ preventScroll: true });
    };

    const renderOffer = () => {
      box.innerHTML = '';
      const emoji = document.createElement('div');
      emoji.className = 'alh-signup__emoji';
      emoji.setAttribute('aria-hidden', 'true');
      emoji.textContent = '🎁';

      const title = document.createElement('strong');
      title.className = 'alh-signup__title';
      title.textContent = 'מגיעה לכם הנחה של 10%';

      const sub = document.createElement('p');
      sub.className = 'alh-signup__sub';
      sub.textContent = 'על ההזמנה הראשונה. רוצים לקבל את הקוד?';

      const yes = document.createElement('button');
      yes.type = 'button';
      yes.className = 'alh-signup__cta';
      yes.textContent = 'כן, אני רוצה!';
      yes.addEventListener('click', renderEmail);

      /* Phrased as a cost, not a dismissal — saying no should mean giving
       * something up, which is the whole device the reference store uses. */
      const no = document.createElement('button');
      no.type = 'button';
      no.className = 'alh-signup__decline';
      no.textContent = 'לא תודה, אשלם מחיר מלא';
      no.addEventListener('click', dismiss);

      box.append(close, emoji, title, sub, yes, no);
      yes.focus({ preventScroll: true });
    };

    overlay.appendChild(box);
    document.body.appendChild(overlay);
    renderOffer();
  }

  function signupBlockedHere() {
    const path = window.location.pathname;
    return (
      /\/(cart|checkout|account)/.test(path) ||
      /[?&]customer_posted=true/.test(window.location.search)
    );
  }

  function initSignupTab() {
    if (!SHOW_SIGNUP_POPUP) return; // tab and popup ship together or not at all
    if (document.querySelector('.alh-signup-tab') || signupBlockedHere()) return;
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'alh-signup-tab';
    tab.innerHTML =
      '<span class="alh-signup-tab__pct">10%</span><span class="alh-signup-tab__txt">הנחה</span>';
    tab.setAttribute('aria-label', 'קבלת 10% הנחה על ההזמנה הראשונה');
    tab.addEventListener('click', openSignupModal);
    document.body.appendChild(tab);
  }

  function initSignupPopup() {
    if (!SHOW_SIGNUP_POPUP || signupSeen() || signupBlockedHere()) return;
    const open = () => {
      if (personalisationStarted()) return;
      if (document.querySelector('.alh-modal')) return;
      openSignupModal();
    };
    window.setTimeout(() => {
      // Never stack the signup popup on top of the cookie banner — wait for
      // the visitor to answer it, then give them a moment before asking again.
      if (document.querySelector('.alh-cookie')) {
        document.addEventListener('alh:cookie-closed', () => window.setTimeout(open, 4000), {
          once: true,
        });
        return;
      }
      open();
    }, SIGNUP_DELAY_MS);
  }

  /* ============================================ journey CTA: back to form ===
   *
   * The closing button of the product-page journey. The visitor is already on
   * the product they want, so navigating anywhere would be a step backwards —
   * it returns them to the buy form and puts the caret in the first field they
   * still have to fill, which is the actual next thing they have to do.
   *
   * A plain <a href="#..."> would need an id on markup Horizon owns and would
   * push a history entry, so the back button would then undo a scroll rather
   * than leave the page. */
  function initJourneyToForm() {
    const buttons = document.querySelectorAll('[data-alh-to-form]');
    if (!buttons.length) return;

    const nextEmptyField = (form) => {
      const fields = [
        form.querySelector(`input[name="properties[${PROP.top}]"]`),
        form.querySelector(`input[name="properties[${PROP.bottom}]"]`),
        form.querySelector(`input[name="properties[${PROP.image}]"]`),
      ];
      return fields.find((el) => el && !el.value) || fields.find(Boolean) || null;
    };

    for (const button of buttons) {
      button.addEventListener('click', () => {
        const form = cartForm();
        if (!form) return;

        const field = nextEmptyField(form);
        const anchor = field ? field.closest('.alh-personalise') || field : form;

        anchor.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'center',
        });

        /* focus() scrolls on its own and would fight the smooth scroll above,
         * so it is deferred and told not to. */
        if (!field) return;
        window.setTimeout(
          () => {
            try {
              field.focus({ preventScroll: true });
            } catch (e) {
              /* file inputs refuse focus in some browsers — harmless */
            }
          },
          reduceMotion ? 0 : 520
        );
      });
    }
  }

  /* ================================================ terms before checkout ===
   *
   * The card processor requires the buyer to tick "I accept the terms" before
   * reaching payment. Horizon's checkout is Shopify's and cannot be edited on
   * this plan, so the gate sits on every road INTO checkout from the theme:
   * the checkout button in the cart drawer and on /cart (both rendered by
   * snippets/cart-summary.liquid into .cart__ctas) and the accelerated PayPal
   * button next to it.
   *
   * The PayPal button is a cross-origin iframe — no click listener can see
   * inside it (that is how it bypassed the photo guard on 2026-08-11). So it is
   * made `inert` until the box is ticked, which removes it from hit-testing
   * and the tab order; a click on it lands on .cart__ctas and shows the error.
   *
   * The drawer is rebuilt from server HTML on every cart change, so the
   * checkbox is injected by JS and re-applied from the shared observer, with
   * the choice kept in sessionStorage so a quantity change doesn't untick it.
   * The acceptance is also written to the cart as an attribute, so it shows on
   * the order in the admin — evidence if a chargeback ever asks for it. */
  const TERMS_KEY = 'alh_terms_ok';
  const TERMS_ATTR = 'אישור תקנון';
  let termsSeq = 0;

  function termsAccepted() {
    try {
      return sessionStorage.getItem(TERMS_KEY) === '1';
    } catch (e) {
      return document.documentElement.dataset.alhTerms === '1';
    }
  }

  function setTermsAccepted(ok) {
    try {
      if (ok) sessionStorage.setItem(TERMS_KEY, '1');
      else sessionStorage.removeItem(TERMS_KEY);
    } catch (e) {
      /* storage blocked — fall back to a page-lifetime flag */
    }
    document.documentElement.dataset.alhTerms = ok ? '1' : '';
    const stamp = ok ? 'כן — ' + new Date().toLocaleString('he-IL') : '';
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [TERMS_ATTR]: stamp } }),
    }).catch(() => {});
  }

  function syncTerms(ctas) {
    const ok = termsAccepted();
    const box = ctas.querySelector('.alh-terms input');
    if (box && box.checked !== ok) box.checked = ok;
    ctas.dataset.alhTerms = ok ? 'ok' : '';
    for (const extra of ctas.querySelectorAll('.additional-checkout-buttons')) {
      if (ok) extra.removeAttribute('inert');
      else extra.setAttribute('inert', '');
    }
    if (ok) {
      const err = ctas.querySelector('.alh-terms__error');
      if (err) err.hidden = true;
    }
  }

  function showTermsError(ctas) {
    const wrap = ctas.querySelector('.alh-terms');
    if (!wrap) return;
    const err = wrap.querySelector('.alh-terms__error');
    err.hidden = false;
    const box = wrap.querySelector('input');
    wrap.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    box.focus({ preventScroll: true });
  }

  function initTermsGate() {
    for (const ctas of document.querySelectorAll('.cart__ctas')) {
      if (!ctas.querySelector('.alh-terms')) {
        termsSeq += 1;
        const id = 'alh-terms-' + termsSeq;
        const wrap = document.createElement('div');
        wrap.className = 'alh-terms';
        wrap.innerHTML =
          '<input type="checkbox" id="' + id + '" required aria-describedby="' + id + '-err">' +
          '<label for="' + id + '">קראתי ואני מאשר/ת את ' +
          '<a href="/pages/terms-of-service" target="_blank" rel="noopener">התקנון</a> ואת ' +
          '<a href="/pages/privacy-policy" target="_blank" rel="noopener">מדיניות הפרטיות</a></label>' +
          '<p class="alh-terms__error" id="' + id + '-err" role="alert" hidden>' +
          'כדי להמשיך לתשלום יש לסמן את אישור התקנון.</p>';
        wrap.querySelector('input').addEventListener('change', (e) => {
          setTermsAccepted(e.target.checked);
          for (const c of document.querySelectorAll('.cart__ctas')) syncTerms(c);
        });
        ctas.prepend(wrap);
        // A click on the inert PayPal area falls through to .cart__ctas.
        ctas.addEventListener('click', (e) => {
          if (termsAccepted()) return;
          if (e.target === ctas || e.target.closest('.additional-checkout-buttons')) {
            showTermsError(ctas);
          }
        });
      }
      syncTerms(ctas);
    }

    if (document.documentElement.dataset.alhTermsGuard === 'true') return;
    document.documentElement.dataset.alhTermsGuard = 'true';
    const guard = (e) => {
      const btn =
        e.type === 'submit'
          ? e.submitter && e.submitter.closest('[name="checkout"], .cart__checkout-button')
          : e.target.closest && e.target.closest('[name="checkout"], .cart__checkout-button');
      if (!btn || termsAccepted()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      const ctas = btn.closest('.cart__ctas') || document.querySelector('.cart__ctas');
      if (ctas) showTermsError(ctas);
    };
    document.addEventListener('click', guard, true);
    document.addEventListener('submit', guard, true);
  }

  /* ======================================================== cookie banner ===
   *
   * Notice + decline, the option the user chose on 2026-09-21. Israel is not a
   * region where Shopify requires prior consent, so tracking runs by default
   * and the banner tells the visitor and lets them turn it off. The decision
   * goes through Shopify's own Customer Privacy API, which is what every web
   * pixel (the Meta pixel included) actually obeys — so "לא מאשר/ת" genuinely
   * stops the tracking rather than just hiding the banner.
   *
   * Seen-state comes from the API's own consent record, not our storage, so it
   * survives on the server; localStorage is only the fallback when the API
   * cannot load. Any link to #alh-cookie-settings reopens the banner. */
  const COOKIE_KEY = 'alh_cookie_choice_v1';

  function withPrivacyApi(cb) {
    const S = window.Shopify;
    if (S && S.customerPrivacy) return cb(S.customerPrivacy);
    if (S && typeof S.loadFeatures === 'function') {
      try {
        S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () =>
          cb((window.Shopify && window.Shopify.customerPrivacy) || null)
        );
        return;
      } catch (e) {
        /* fall through */
      }
    }
    cb(null);
  }

  function cookieDecided(api) {
    if (api && typeof api.currentVisitorConsent === 'function') {
      try {
        const c = api.currentVisitorConsent() || {};
        if (c.marketing === 'yes' || c.marketing === 'no') return true;
      } catch (e) {
        /* fall back to storage */
      }
    }
    try {
      return !!localStorage.getItem(COOKIE_KEY);
    } catch (e) {
      return false;
    }
  }

  function saveCookieChoice(api, ok) {
    try {
      localStorage.setItem(COOKIE_KEY, ok ? 'yes' : 'no');
    } catch (e) {
      /* storage blocked — the API record still holds the choice */
    }
    if (api && typeof api.setTrackingConsent === 'function') {
      try {
        api.setTrackingConsent(
          { analytics: ok, marketing: ok, preferences: ok, sale_of_data: ok },
          () => {}
        );
      } catch (e) {
        /* nothing else to do — the banner must still close */
      }
    }
  }

  function closeCookieBanner() {
    const el = document.querySelector('.alh-cookie');
    if (el) el.remove();
    document.dispatchEvent(new CustomEvent('alh:cookie-closed'));
  }

  function openCookieBanner(api) {
    if (document.querySelector('.alh-cookie')) return;
    const bar = document.createElement('div');
    bar.className = 'alh-cookie';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'הודעה על שימוש בעוגיות');
    bar.innerHTML =
      '<p class="alh-cookie__text">אנחנו משתמשים בעוגיות (Cookies) כדי שהאתר יעבוד, ' +
      'וכדי למדוד ולשפר את האתר והפרסום. אפשר לסרב לעוגיות שאינן הכרחיות. ' +
      '<a href="/pages/privacy-policy#cookies">פרטים במדיניות הפרטיות</a></p>' +
      '<div class="alh-cookie__btns">' +
      '<button type="button" class="alh-cookie__yes">מאשר/ת</button>' +
      '<button type="button" class="alh-cookie__no">לא מאשר/ת</button></div>';
    bar.querySelector('.alh-cookie__yes').addEventListener('click', () => {
      saveCookieChoice(api, true);
      closeCookieBanner();
    });
    bar.querySelector('.alh-cookie__no').addEventListener('click', () => {
      saveCookieChoice(api, false);
      closeCookieBanner();
    });
    document.body.appendChild(bar);
  }

  function initCookieBanner() {
    if (document.documentElement.dataset.alhCookie === 'true') return;
    document.documentElement.dataset.alhCookie = 'true';
    withPrivacyApi((api) => {
      if (!cookieDecided(api)) openCookieBanner(api);
      document.addEventListener('click', (e) => {
        const a = e.target.closest && e.target.closest('a[href$="#alh-cookie-settings"]');
        if (!a) return;
        e.preventDefault();
        openCookieBanner(api);
        const first = document.querySelector('.alh-cookie__yes');
        if (first) first.focus();
      });
    });
  }

  function init() {
    initCookieBanner();
    initTermsGate();
    initLanguageNotice();
    initLaunchPopup();
    initDeliveryEta();
    initReveal();
    initAbout();
    initJourneyToForm();
    initContactForm();
    initReviewsCarousel();
    initSwatches();
    watchSwatches();
    initPersonalisation();
    initQuantityChoice();
    initCartUploads();
    initDrawerJourney();
    initNewsletterReward();
    initSignupTab();
    initSignupPopup();
    initStickyAddToCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
